/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import {ref, computed} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import useLocale from '@/core/util/composable/useLocale';
import useDateFormat from '@/core/util/composable/useDateFormat';
import usei18n from '@/core/util/composable/usei18n';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import {publishBuzzUnreadCount} from '@/orangehrmBuzzPlugin/util/buzzUnreadCount';

const DEFAULT_PAGE_SIZE = 10;

export default function useBuzzNotifications(options = {}) {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const {locale} = useLocale();
  const {jsDateFormat, jsTimeFormat} = useDateFormat();
  const {$t} = usei18n();
  const {$tEmpName} = useEmployeeNameTranslate();
  const http = new APIService(
    window.appGlobal.baseUrl,
    '/api/v2/buzz/notifications',
  );
  const markAllHttp = new APIService(
    window.appGlobal.baseUrl,
    '/api/v2/buzz/notifications/mark-all-read',
  );

  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const notifications = ref([]);
  const unreadCount = ref(0);
  const totalCount = ref(0);

  const isEmpty = computed(
    () => !isLoading.value && notifications.value.length === 0,
  );

  const hasMore = computed(() => notifications.value.length < totalCount.value);

  const emitUnreadCount = (count) => {
    options.onUnreadCount?.(count);
    publishBuzzUnreadCount(count);
  };

  const fetchNotifications = ({reset = true} = {}) => {
    if (reset) {
      isLoading.value = true;
    } else {
      isLoadingMore.value = true;
    }

    const offset = reset ? 0 : notifications.value.length;

    return http
      .request({
        method: 'GET',
        params: {limit: pageSize, offset},
      })
      .then((response) => {
        const {data, meta} = response.data;
        const items = Array.isArray(data) ? data : [];

        totalCount.value = meta?.total ?? items.length;
        notifications.value = reset
          ? items
          : [...notifications.value, ...items];
        unreadCount.value = meta?.unreadCount ?? 0;
        emitUnreadCount(unreadCount.value);
      })
      .finally(() => {
        isLoading.value = false;
        isLoadingMore.value = false;
      });
  };

  const loadMoreNotifications = () => {
    if (!hasMore.value || isLoadingMore.value || isLoading.value) {
      return Promise.resolve();
    }

    return fetchNotifications({reset: false});
  };

  const performerName = (performer) => {
    if (!performer) {
      return '';
    }

    return $tEmpName(
      {
        firstName: performer.firstName,
        lastName: performer.lastName,
        middleName: performer.middleName ?? null,
        nickName: performer.nickName ?? null,
        nickname: performer.nickname ?? null,
        terminationId: performer.terminationId ?? null,
      },
      {
        preferNickname: true,
      },
    );
  };

  const notificationMessage = (item) => {
    const name = performerName(item.performer);
    const messageKeys = {
      admin_post: 'buzz.notification_admin_post',
      comment: 'buzz.notification_comment',
      like_share: 'buzz.notification_like_share',
      like_comment: 'buzz.notification_like_comment',
      mention: 'buzz.notification_mention',
    };

    const key = messageKeys[item.type];
    if (!key) {
      return name;
    }

    return $t(key, {performerName: name});
  };

  const formatNotificationTime = (item) => {
    if (!item.createdDate || !item.createdTime) {
      return item.createdTime ?? '';
    }

    const utcDate = parseDate(
      `${item.createdDate} ${item.createdTime} +00:00`,
      'yyyy-MM-dd HH:mm xxx',
    );
    if (!utcDate) {
      return item.createdTime;
    }

    return formatDate(utcDate, `${jsDateFormat} ${jsTimeFormat}`, {locale});
  };

  const markAsRead = (item) => {
    if (item.isRead) {
      return Promise.resolve();
    }

    return http.update(item.id, {isRead: true}).then(() => {
      item.isRead = true;
      unreadCount.value = Math.max(0, unreadCount.value - 1);
      emitUnreadCount(unreadCount.value);
    });
  };

  const onMarkAllRead = () => {
    return markAllHttp.create({}).then(() => {
      notifications.value = notifications.value.map((item) => ({
        ...item,
        isRead: true,
      }));
      unreadCount.value = 0;
      emitUnreadCount(0);
    });
  };

  return {
    isLoading,
    isLoadingMore,
    notifications,
    unreadCount,
    totalCount,
    isEmpty,
    hasMore,
    fetchNotifications,
    loadMoreNotifications,
    notificationMessage,
    formatNotificationTime,
    markAsRead,
    onMarkAllRead,
  };
}
